import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn } from "lucide-react";

/**
 * A thumbnail that opens its own full-size overlay — used wherever a back-office
 * or partner user needs to actually look at the bouquet.
 */
export const ZoomImage = ({ src, alt = "", className = "", caption = "", testid = "zoom-image" }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!src) return null;

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        title={caption || "Click to see it full size"}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        className={`group relative block overflow-hidden cursor-zoom-in ${className}`}
      >
        <img src={src} alt={alt} loading="lazy"
             className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
        <span className="absolute inset-0 hidden sm:flex items-center justify-center bg-ink/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <ZoomIn size={14} className="text-white" />
        </span>
      </button>

      {open
        ? createPortal(
          <div
            data-testid={`${testid}-overlay`}
            role="dialog"
            aria-modal="true"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-ink/85 backdrop-blur-md p-6 animate-in fade-in duration-200"
          >
            <figure
              className="flex flex-col gap-3 items-center animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={src} alt={alt}
                   className="max-h-[82vh] max-w-[min(92vw,860px)] w-auto object-contain shadow-2xl" />
              {caption ? <figcaption className="text-xs text-plaster/90 text-center max-w-[60ch]">{caption}</figcaption> : null}
            </figure>
            <button type="button" data-testid={`${testid}-close`} aria-label="Close"
                    onClick={() => setOpen(false)}
                    className="absolute top-5 end-5 text-plaster hover:text-white transition-colors">
              <X size={22} />
            </button>
          </div>,
          document.body,
        )
        : null}
    </>
  );
};

export default ZoomImage;
